import { Button } from '@act/storybook';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { needRequests } from 'redux/requests/need.requests';
import { useAppDispatch } from 'redux/storeHooks';
import { getMemberNeeds, needActions } from 'redux/slices/needs.slice';
import { useToast } from 'react-components/app/ReusableToast';

interface ConsolidateDuplicateNeedsDialogProps {
  open: boolean;
  onClose: () => void;
  memberId: string;
  onSuccess?: () => void;
}

export const ConsolidateDuplicateNeedsDialog = ({
  open,
  onClose,
  memberId,
  onSuccess,
}: ConsolidateDuplicateNeedsDialogProps) => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const [selectedDate, setSelectedDate] = useState<string>('');

  const [consolidateResult, isConsolidating, consolidateNeeds] = useApiCall(
    needRequests.consolidateDuplicateNeeds
  );

  useEffect(() => {
    if (consolidateResult && !isConsolidating) {
      const deletedCount = consolidateResult.deletedNeedIds?.length || 0;
      toast(
        deletedCount > 0
          ? `Successfully consolidated ${deletedCount} duplicate need(s)`
          : 'No duplicate needs found'
      );

      // Remove deleted needs from Redux store immediately
      // The backend broadcasts updates for existing needs via websocket,
      // but doesn't send NEED_DELETED events for soft-deleted needs during consolidation
      if (deletedCount > 0 && consolidateResult.deletedNeedIds) {
        consolidateResult.deletedNeedIds.forEach(needId => {
          dispatch(needActions.userNeedDeleted({ needId }));
        });
      }

      // Refresh needs list to ensure all updates are reflected
      // The websocket should handle updates for the consolidated need,
      // but we refresh as a fallback to ensure consistency
      dispatch(getMemberNeeds({ params: { memberId } }));

      onSuccess?.();
      onClose();
    }
  }, [consolidateResult, isConsolidating, toast, dispatch, memberId, onSuccess, onClose]);

  const handleConsolidate = () => {
    consolidateNeeds({
      body: {
        memberId,
        ...(selectedDate && { date: selectedDate }),
      },
    });
  };

  const handleClose = () => {
    if (!isConsolidating) {
      setSelectedDate('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="titleLarge">Consolidate Duplicate Needs</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="bodyMedium" sx={{ marginBottom: '16px' }}>
          Consolidate duplicate needs for this member. If a date is selected, the system
          will find needs with the same issue created on that date. If no date is selected,
          it will consolidate all duplicate needs across all dates. The system will keep
          the earliest need and move all actions to it.
        </Typography>
        <input
          type="date"
          value={selectedDate}
          onChange={e => setSelectedDate(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            fontSize: '16px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            marginBottom: '8px',
          }}
        />
        <Typography variant="bodySmall" color="text.secondary">
          Leave date empty to consolidate all duplicates across all dates
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="text" disabled={isConsolidating}>
          Cancel
        </Button>
        <Button
          onClick={handleConsolidate}
          variant="contained"
          disabled={isConsolidating}
          debounced
        >
          {isConsolidating ? 'Consolidating...' : 'Consolidate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

